import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FileUpload } from './components/FileUpload';
import { PrintPage } from './components/PrintPage';
import { parseExcelFile, type CellData } from './lib/excel';
import { Printer, RefreshCw, Table2, CheckSquare, Square, Check, RectangleVertical, RectangleHorizontal } from 'lucide-react';
import { FeedbackButton } from './components/FeedbackButton';
import { Footer } from './components/Footer';

function App() {
  const [cells, setCells] = useState<CellData[]>([]);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [paperFormat, setPaperFormat] = useState<string>('A4');
  const [textScale, setTextScale] = useState<number>(1.0); // 100% = default (Large)
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileName, setFileName] = useState<string>('');

  // Paper format dimensions (width x height in portrait)
  const paperFormats: Record<string, { width: string; height: string; css: string }> = {
    'A3': { width: '297mm', height: '420mm', css: 'A3' },
    'A4': { width: '210mm', height: '297mm', css: 'A4' },
    'A5': { width: '148mm', height: '210mm', css: 'A5' },
    'Letter': { width: '8.5in', height: '11in', css: '8.5in 11in' },
    'Legal': { width: '8.5in', height: '14in', css: '8.5in 14in' },
    'Tabloid': { width: '11in', height: '17in', css: '11in 17in' },
  };

  const handleFileSelect = async (file: File) => {
    setIsProcessing(true);
    setFileName(file.name);
    try {
      const parsedCells = await parseExcelFile(file);
      setCells(parsedCells);
      setSelectedCells(new Set(parsedCells.map(cell => cell.id)));
      
      // Track file upload
      if (window.plausible) {
        window.plausible('File Uploaded', {
          props: {
            fileName: file.name,
            totalPages: parsedCells.length
          }
        });
      }
    } catch (error) {
      console.error('Failed to parse Excel file:', error);
      
      // Track upload error
      if (window.plausible) {
        window.plausible('Upload Error', {
          props: {
            fileName: file.name,
            fileType: file.type || 'unknown',
            errorMessage: error instanceof Error ? error.message : 'Unknown error'
          }
        });
      }
      
      alert('Failed to parse Excel file. Please make sure it\'s a valid .xlsx file.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePrint = () => {
    // Track print event
    if (window.plausible) {
      window.plausible('Print Started', {
        props: {
          fileName: fileName,
          paperFormat: paperFormat,
          orientation: orientation,
          textScale: textScale,
          selectedPages: selectedCells.size,
          totalPages: cells.length,
          initiatedBy: 'button'
        }
      });
    }
    window.print();
  };

  const handleReset = () => {
    // Track reset event
    if (window.plausible) {
      window.plausible('Reset');
    }
    
    setCells([]);
    setSelectedCells(new Set());
    setFileName('');
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedCells);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedCells(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedCells.size === cells.length) {
      setSelectedCells(new Set());
    } else {
      setSelectedCells(new Set(cells.map(c => c.id)));
    }
  };

  // Track browser-initiated prints (Ctrl+P, browser menu, etc.)
  useEffect(() => {
    const handleBeforePrint = () => {
      if (window.plausible && cells.length > 0) {
        window.plausible('Print Started', {
          props: {
            fileName: fileName,
            paperFormat: paperFormat,
            orientation: orientation,
            textScale: textScale,
            selectedPages: selectedCells.size,
            totalPages: cells.length,
            initiatedBy: 'browser'
          }
        });
      }
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    return () => window.removeEventListener('beforeprint', handleBeforePrint);
  }, [cells.length, fileName, paperFormat, orientation, textScale, selectedCells.size]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans selection:bg-[#21A366]/20 selection:text-[#21A366] flex flex-col">
      <style>{`
        @page { 
          size: ${paperFormats[paperFormat]?.css || 'A4'} ${orientation}; 
          margin: 0;
        }
        .print-page { 
          width: ${orientation === 'portrait' 
            ? paperFormats[paperFormat]?.width 
            : paperFormats[paperFormat]?.height
          }; 
          height: ${orientation === 'portrait'
            ? paperFormats[paperFormat]?.height
            : paperFormats[paperFormat]?.width
          }; 
        }
      `}</style>
      
      {/* Screen Interface - Valid for non-print only due to CSS media query in index.css hiding body > * */}
      <div className="no-print flex flex-col" style={{ minHeight: 'calc(100vh - 69px)' }}>
        {/* Header */}
        <header className="py-5 px-8 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 sticky top-0 z-50 shadow-sm">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <button 
              onClick={handleReset}
              className="text-left flex items-center gap-4 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#21A366] rounded-xl blur-md opacity-20"></div>
                <div className="relative bg-gradient-to-br from-[#21A366] to-[#107C41] p-3 rounded-xl shadow-lg">
                  <Table2 className="w-7 h-7 text-white" strokeWidth={2.5} />
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">
                  Spreadsheet Poster Print
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">Transform Excel files into beautiful posters</p>
              </div>
            </button>
            
            {cells.length > 0 && (
              <div className="flex items-center gap-3">
                {/* Empty - all controls moved to sidebar */}
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex overflow-hidden">
          {cells.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 animate-in fade-in zoom-in duration-500">
               <div className="text-center mb-12">
                   <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#21A366] to-[#107C41] bg-clip-text text-transparent">
                    Transform Excel Files into Posters
                  </h2>
                  <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
                    Upload your spreadsheet and we'll automatically generate a printable poster for every cell.
                    Choose from multiple paper formats including A3, A4, A5, Letter, Legal, and Tabloid.
                  </p>
               </div>
               <FileUpload onFileSelect={handleFileSelect} isProcessing={isProcessing} />
            </div>
          ) : (
            <div className="flex-1 flex justify-center bg-slate-50 dark:bg-slate-950">
              <div className="flex w-full max-w-[1600px]">
                {/* Sidebar with white background */}
                <div className="bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
                  <aside className="w-72 p-6 overflow-y-auto h-full">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">Print Settings</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Configure your poster output</p>

                  {/* Selection */}
                  <div className="mb-6">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 block">Selection</label>
                    <div className="space-y-2">
                      <button
                        onClick={toggleSelectAll}
                        className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-all group"
                      >
                        {selectedCells.size === cells.length ? (
                          <CheckSquare className="w-5 h-5 text-[#21A366]" strokeWidth={2} />
                        ) : (
                          <Square className="w-5 h-5 text-slate-400" strokeWidth={2} />
                        )}
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {selectedCells.size === cells.length ? 'Deselect All' : 'Select All'}
                        </span>
                      </button>
                      <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {selectedCells.size} of {cells.length} selected
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Orientation */}
                  <div className="mb-6">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 block">Orientation</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setOrientation('portrait')}
                        className={`px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                          orientation === 'portrait'
                            ? 'bg-[#21A366] text-white shadow-md'
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        <RectangleVertical className="w-5 h-5 mx-auto mb-1" strokeWidth={2} />
                        Portrait
                      </button>
                      <button
                        onClick={() => setOrientation('landscape')}
                        className={`px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                          orientation === 'landscape'
                            ? 'bg-[#21A366] text-white shadow-md'
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        <RectangleHorizontal className="w-5 h-5 mx-auto mb-1" strokeWidth={2} />
                        Landscape
                      </button>
                    </div>
                  </div>

                  {/* Paper Format */}
                  <div className="mb-6">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 block">Paper Format</label>
                    <select
                      value={paperFormat}
                      onChange={(e) => setPaperFormat(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg font-medium text-sm bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-700 focus:border-[#21A366] focus:ring-2 focus:ring-[#21A366]/20 outline-none transition-all cursor-pointer"
                    >
                      <optgroup label="A Series (ISO)">
                        <option value="A3">A3 (297×420mm)</option>
                        <option value="A4">A4 (210×297mm)</option>
                        <option value="A5">A5 (148×210mm)</option>
                      </optgroup>
                      <optgroup label="US Paper Sizes">
                        <option value="Letter">Letter (8.5×11 in)</option>
                        <option value="Legal">Legal (8.5×14 in)</option>
                        <option value="Tabloid">Tabloid (11×17 in)</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Text Size */}
                  <div className="mb-6">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 block">Text Size</label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => setTextScale(1.0)}
                        className={`px-3 py-3 rounded-lg font-medium text-xs transition-all ${
                          textScale === 1.0
                            ? 'bg-[#21A366] text-white shadow-md'
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        Large
                      </button>
                      <button
                        onClick={() => setTextScale(0.80)}
                        className={`px-3 py-3 rounded-lg font-medium text-xs transition-all ${
                          textScale === 0.80
                            ? 'bg-[#21A366] text-white shadow-md'
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        Medium
                      </button>
                      <button
                        onClick={() => setTextScale(0.60)}
                        className={`px-3 py-3 rounded-lg font-medium text-xs transition-all ${
                          textScale === 0.60
                            ? 'bg-[#21A366] text-white shadow-md'
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        Small
                      </button>
                    </div>
                  </div>

                  {/* File Info */}
                  <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Current File</p>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{fileName}</p>
                  </div>

                  {/* Print Button */}
                  <button
                    onClick={handlePrint}
                    disabled={selectedCells.size === 0}
                    className="w-full px-6 py-4 bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold shadow-lg shadow-slate-900/30 transition-all active:scale-95 flex items-center justify-center gap-3 mb-3"
                  >
                    <Printer className="w-5 h-5" strokeWidth={2.5} />
                    Print {selectedCells.size} Poster{selectedCells.size !== 1 ? 's' : ''}
                  </button>

                  {/* Reset Button */}
                  <button
                    onClick={handleReset}
                    className="w-full px-4 py-3 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center justify-center gap-2 border border-slate-200 dark:border-slate-700"
                  >
                    <RefreshCw className="w-4 h-4" strokeWidth={2} />
                    Reset
                  </button>
                  </aside>
                </div>

                {/* Main content area */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-8">
                  <div className="mb-6">
                    <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">
                      Preview
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Click on any poster to select or deselect it for printing
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-20">
                    {cells.map((cell) => {
                      const isSelected = selectedCells.has(cell.id);
                      return (
                        <div 
                          key={cell.id} 
                          onClick={() => toggleSelection(cell.id)}
                          className={`
                            group relative ${orientation === 'portrait' ? 'aspect-[1/1.414]' : 'aspect-[1.414/1]'} bg-white dark:bg-slate-800 rounded-xl shadow-sm border-2 overflow-hidden cursor-pointer transition-all duration-300
                            ${isSelected ? 'border-[#21A366] ring-4 ring-[#21A366]/20 shadow-lg opacity-100' : 'border-slate-200 dark:border-slate-700 hover:border-[#21A366]/50 hover:shadow-md opacity-50 hover:opacity-75'}
                          `}
                        >
                          {/* Checkmark indicator for selected items */}
                          {isSelected && (
                            <div className="absolute top-3 right-3 z-10 w-8 h-8 bg-[#21A366] rounded-full flex items-center justify-center shadow-lg">
                              <Check className="w-5 h-5 text-white" strokeWidth={3} />
                            </div>
                          )}

                          {/* Preview content - normalized to A4 aspect ratio for consistent preview */}
                          <div className="absolute inset-0 p-4 flex items-center justify-center overflow-hidden">
                            {/* Fixed A4 aspect container for preview consistency */}
                            <div className="w-full h-full flex items-center justify-center">
                              <div 
                                className="bg-white flex items-center justify-center text-center"
                                style={{
                                  width: orientation === 'portrait' ? '210mm' : '297mm',
                                  height: orientation === 'portrait' ? '297mm' : '210mm',
                                  transform: 'scale(0.25)',
                                  transformOrigin: 'center',
                                }}
                              >
                                <PrintPage value={cell.value} scale={textScale} />
                              </div>
                            </div>
                          </div>
                          
                          <div className="absolute bottom-3 left-3 text-[10px] font-mono text-slate-500 bg-white/90 dark:bg-slate-900/90 px-2 py-1 rounded backdrop-blur-sm">
                            {cell.id.split('!')[1]}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            </div>
          )}

        </main>
      </div>

      {/* Floating Feedback Button */}
      <FeedbackButton />

      {/* Footer */}
      <Footer />

      {/* Print Container - Rendered via Portal to be direct child of body */}
      {/* We use opacity-0 pointer-events-none instead of hidden so the layout is calculated for text fitting */}
      {createPortal(
        <div id="print-container" className="absolute top-0 left-[-10000px] w-auto h-auto opacity-0 pointer-events-none print:opacity-100 print:pointer-events-auto print:left-0 print:top-0 z-[-50]">
          {cells.filter(cell => selectedCells.has(cell.id)).map((cell) => (
            <PrintPage key={cell.id} value={cell.value} scale={textScale} />
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default App;
